import React, { useEffect } from 'react';
import { Shield, Heart, AlertTriangle, ArrowRight, Leaf, Share2, Facebook, Twitter, Mail, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import NavBar from '@/components/NavBar';
// Removed Footer import - App.tsx handles global footer
import { BOOKING_URL } from '@/constants/urls';
import { WelcomeModal } from '@/components/WelcomeModal';
import BlogPostingSchema from '@/components/BlogPostingSchema';
import toxinsImage from '@/assets/blog/eliminate-toxins.webp';

const ToxinsFertilityBlog = () => {
  // Force scroll to top on page load (ignoring anchors)
  useEffect(() => {
    window.scrollTo(0, 0);

    // Update document title and meta tags
    document.title = "Toxins & Fertility Currimundi | Environmental Health Guide";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Learn about environmental toxins that impact fertility at our Currimundi Chinese Medicine clinic. Reduce exposure and support reproductive health with expert guidance.");
    }

    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', "Toxins & Fertility Currimundi | Environmental Health Guide");
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', "Learn about environmental toxins that impact fertility at our Currimundi Chinese Medicine clinic. Reduce exposure and support reproductive health with expert guidance.");
    }

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', "https://womenandtheway.com.au/blog/toxins-fertility");
    }

    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) {
      ogImage.setAttribute('content', toxinsImage);
    }

    // Update Twitter Card
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', "Toxins & Fertility Currimundi | Environmental Health Guide");
    }

    // Cleanup function
    return () => {
      // Reset title on unmount
      document.title = "Acupuncture Sunshine Coast & Caloundra | Women and The Way - Chinese Medicine";
    };
  }, []);

  const handleShare = (platform: string) => {
    const url = window.location.href;
    const title = "Toxins & Fertility | Women and the Way";
    const text = "Learn about common endocrine-disrupting chemicals and how to reduce your exposure for better fertility health.";
    
    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'email':
        const subject = "Toxins & Fertility";
        const body = `${text}\n\nRead more: ${url}`;
        window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
        break;
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  return (
    <>
      <BlogPostingSchema
        title="How Environmental Toxins Affect Fertility"
        description="Discover how environmental toxins impact fertility and learn practical steps to reduce exposure for better reproductive health."
        image={toxinsImage}
        url="https://womenandtheway.com.au/blog/toxins-fertility"
        publishedDate="2025-01-10"
        modifiedDate="2025-01-27"
        keywords="environmental toxins, fertility, reproductive health, women's health, Sunshine Coast, fertility clinic, Chinese medicine, detox"
        articleSection="Fertility & Women's Health"
      />
      <NavBar />
      <div className="min-h-screen bg-warm-beige pt-24 md:pt-32">
        {/* Hero Section with Image */}
        <section className="relative py-16 px-6 bg-gradient-to-r from-sage-50 via-white to-amber-50">
          <div className="container mx-auto max-w-6xl">
            <div className="flex flex-col md:flex-row items-center md:justify-between">
              <div className="w-full md:w-1/2 mb-10 md:mb-0">
                <div className="mb-8">
                  <span className="text-5xl md:text-6xl mb-4 block animate-float">⚠️</span>
                  <h1 className="font-serif text-4xl md:text-5xl font-bold mb-6 leading-tight bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                    Toxins & Fertility
                  </h1>
                  <p className="text-xl text-gray-600 font-light">
                    How to Reduce Your Exposure for Reproductive Health
                  </p>
                </div>
              </div>
              <div className="w-full md:w-5/12 relative">
                <div className="rounded-2xl overflow-hidden shadow-2xl border-4 border-amber-200/50">
                  <img 
                    src={toxinsImage} 
                    alt="How toxins impact fertility at Women and the Way, Sunshine Coast – understanding environmental factors and fertility health" 
                    className="w-full h-auto object-cover aspect-[4/3]"
                  />
                </div>
                <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-lg shadow-lg border border-amber-200/50 w-3/4 md:w-1/2">
                  <p className="text-sage-800 font-serif font-medium">
                    "Small changes, big impact - protect your future family."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <main className="py-12 px-6">
          <div className="container mx-auto max-w-3xl">
            {/* Introduction */}
            <div className="prose prose-lg max-w-none mb-16">
              <p className="text-lg leading-relaxed text-gray-700 mb-6">
                Everyday toxins are all around us - in our food, water, air, and even personal care products. At Women and the Way, we help you identify and reduce these exposures to support your fertility and overall wellbeing.
              </p>
              <p className="text-lg leading-relaxed text-gray-700 mb-6">
                Research shows that certain chemicals can disrupt hormones, affect egg and sperm quality, and increase the risk of fertility challenges.
              </p>
              <p className="text-lg leading-relaxed text-gray-700">
                Here's how you can take simple, practical steps to create a safer environment for yourself and your future family.
              </p>
            </div>

            {/* What are Toxins? */}
            <section className="mb-16">
              <h2 className="font-serif text-3xl font-bold mb-8 flex items-center">
                <span className="text-3xl mr-3">🧪</span>
                <span className="bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">What are Toxins?</span>
              </h2>
              <Card className="bg-white border-2 border-amber-200/50 shadow-sm mb-8 rounded-2xl">
                <CardContent className="p-8">
                  <p className="text-lg leading-relaxed text-gray-700 mb-6">
                    Toxins are substances that can harm your body's natural balance. Some are unavoidable, but many exposures can be reduced with awareness and small changes.
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <ArrowRight className="w-5 h-5 text-sage-green mt-1 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">Plastics (BPA, phthalates)</span>
                    </li>
                    <li className="flex items-start">
                      <ArrowRight className="w-5 h-5 text-sage-green mt-1 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">Pesticides & herbicides</span>
                    </li>
                    <li className="flex items-start">
                      <ArrowRight className="w-5 h-5 text-sage-green mt-1 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">Heavy metals (lead, mercury)</span>
                    </li>
                    <li className="flex items-start">
                      <ArrowRight className="w-5 h-5 text-sage-green mt-1 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">Fragrances & parabens</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </section>

            {/* How to Reduce Exposure */}
            <section className="mb-16">
              <h2 className="font-serif text-3xl font-bold mb-8 flex items-center">
                <span className="text-3xl mr-3">🛡️</span>
                <span className="bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">How to Reduce Exposure</span>
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <Card className="bg-soft-pink border-2 border-amber-200/50 shadow-sm rounded-2xl">
                  <CardContent className="p-8">
                    <h3 className="font-serif text-2xl font-semibold mb-2 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Eat Clean</h3>
                    <p className="text-gray-700 mb-4">Choose organic when possible, wash produce, and avoid processed foods.</p>
                  </CardContent>
                </Card>
                <Card className="bg-white border-2 border-amber-200/50 shadow-sm rounded-2xl">
                  <CardContent className="p-8">
                    <h3 className="font-serif text-2xl font-semibold mb-2 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Detox Your Home</h3>
                    <p className="text-gray-700 mb-4">Use natural cleaning products, filter your water, and ventilate your space.</p>
                  </CardContent>
                </Card>
              </div>
              <div className="mt-8 p-6 bg-sage-green/10 rounded-lg border-2 border-amber-200/50">
                <p className="text-gray-700">
                  <strong>Tip:</strong> Swap plastic containers for glass or stainless steel.
                </p>
              </div>
            </section>

            {/* Share Component */}
            <div className="my-8 text-center">
              <div className="bg-white rounded-2xl p-6 shadow-sm border-2 border-amber-200/50">
                <h3 className="font-serif text-xl font-semibold mb-4 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">Share this article</h3>
                <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleShare('facebook')}
                    className="flex items-center space-x-2 border-sage-green text-sage-green hover:bg-sage-green hover:text-white whitespace-nowrap"
                  >
                    <Facebook className="w-4 h-4" />
                    <span>Facebook</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleShare('twitter')}
                    className="flex items-center space-x-2 border-sage-green text-sage-green hover:bg-sage-green hover:text-white whitespace-nowrap"
                  >
                    <Twitter className="w-4 h-4" />
                    <span>Twitter/X</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleShare('email')}
                    className="flex items-center space-x-2 border-sage-green text-sage-green hover:bg-sage-green hover:text-white whitespace-nowrap"
                  >
                    <Mail className="w-4 h-4" />
                    <span>Email</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopyLink}
                    className="flex items-center space-x-2 border-sage-green text-sage-green hover:bg-sage-green hover:text-white whitespace-nowrap"
                  >
                    <Copy className="w-4 h-4" />
                    <span>Copy Link</span>
                  </Button>
                </div>
              </div>
            </div>

            {/* Call to Action */}
            <section className="text-center py-16">
              <div className="bg-sage-800 rounded-2xl p-12 shadow-lg border-2 border-amber-200/50 max-w-2xl mx-auto">
                <h2 className="font-serif text-3xl font-bold text-white mb-6">Ready to Reduce Toxins for Fertility?</h2>
                <p className="text-lg text-sage-100 mb-8 max-w-2xl mx-auto">
                  Every small change you make today can have a big impact on your fertility and your future family's health. Start your journey now!
                </p>
                <Button 
                  size="lg" 
                  className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-4 text-lg font-semibold rounded-full"
                  onClick={() => window.open(BOOKING_URL, '_blank')}
                >
                  Book Now
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <p className="text-sm text-sage-200 mt-4">Protect your fertility, protect your future</p>
              </div>
            </section>
          </div>
        </main>
      </div>
              {/* Footer removed - App.tsx handles global footer */}
      <WelcomeModal />
    </>
  );
};

export default ToxinsFertilityBlog; 